import { FormManager } from 'react-form-state-manager';
import { Trans, useTranslation } from 'react-i18next';

import { GetProductQuery as GetProduct } from '__generated__/graphql';

import { ErrorBag } from '../../../../common/helpers';
import { TranslatableTextArea } from '../../../Translations/Inputs';
import UI from '../../../../common/UI';
import useNumberInput from '../../../../common/useNumberInput';
import useProject from '../../../useProject';

type Event = GetProduct['event'];

interface FormData {
  vat_percentage?: number;
  hide_on_invoice: boolean;
  invoice_text?: string;
  ledger_account?: string;
}

export interface EditProductPricingFormProps {
  event: Event;
  form: FormManager<FormData>;
  errors: ErrorBag;
  withoutVat?: boolean;
}

const ProductInvoiceOptionsForm = ({ event, form, errors, withoutVat = false }: EditProductPricingFormProps) => {
  const { t } = useTranslation();
  const project = useProject();
  const numberInput = useNumberInput();

  const hasVatNumber = Boolean(event.vat_number || project.organisation_vat_number);

  return (
    <>
      <UI.Div>
        <UI.Legend sc={{ block: true, mb: 1 }}>
          {t('invoicing')}
        </UI.Legend>
      </UI.Div>

      {!withoutVat && (
        <UI.Div>
          <UI.InputGroup>
            <UI.InputLabel>{t('vat_rate')}</UI.InputLabel>
            <UI.InputDescription>
              {hasVatNumber && t('vat_description')}
              {!hasVatNumber && (
                <Trans i18nKey="charging_vat_description">
                  <UI.Link to={`/${project.id}/events/${event.id}/settings/payments`} />
                </Trans>
              )}
            </UI.InputDescription>
            {hasVatNumber && (
              <UI.FormGrid sc={{ columns: ['1fr', '1fr 1fr'] }}>
                <UI.InputWrapper>
                  <UI.Input
                    {...form.text(
                      'vat_percentage',
                      { inputHandler: numberInput({ min: 0, max: 99.999, decimals: 3 }) },
                    )}
                    sc={{ pr: 4 }}
                  />
                  <span>%</span>
                </UI.InputWrapper>
                <UI.ErrorMessages attribute={t('vat_rate')} errors={errors.vat_percentage} />
              </UI.FormGrid>
            )}
          </UI.InputGroup>
        </UI.Div>
      )}

      <UI.InputGroup sc={{ valid: !errors.hide_on_invoice, touched: form.touched.hide_on_invoice }}>
        <UI.InputLabel>{t('hide_on_invoice')}</UI.InputLabel>
        <UI.InputDescription>
          {t('hide_on_invoice_product_description')}
        </UI.InputDescription>
        <UI.Checkbox {...form.checkbox('hide_on_invoice')}>
          {t('hide_on_invoice')}
        </UI.Checkbox>
        <UI.ErrorMessages attribute={t('hide_on_invoice')} errors={errors.hide_on_invoice} />
      </UI.InputGroup>

      {!form.values.hide_on_invoice && (
        <UI.FormGrid>
          <UI.InputGroup sc={{ valid: !errors.invoice_text, touched: form.touched.invoice_text }}>
            <UI.InputLabel htmlFor="InvoiceText">
              {t('invoice_text')}
            </UI.InputLabel>
            <UI.InputDescription>
              {t('invoice_text_description')}
            </UI.InputDescription>
            <TranslatableTextArea
              form={form}
              errors={errors}
              name="invoice_text"
              title={t('invoice_text')}
              defaultLocale={event.locale}
              id="InvoiceText"
            />
            <UI.ErrorMessages attribute={t('invoice_text')} errors={errors.invoice_text} />
          </UI.InputGroup>
        </UI.FormGrid>
      )}

      <UI.HR />

      <UI.Div>
        <UI.Legend sc={{ block: true, mb: 1 }}>
          {t('ledger_account')}
        </UI.Legend>
        <UI.InputDescription>
          {t('ledger_account_description')}
        </UI.InputDescription>
      </UI.Div>

      <UI.FormGrid sc={{ columns: ['1fr', '1fr 1fr'] }}>
        <UI.InputGroup sc={{ valid: !errors.ledger_account, touched: form.touched.ledger_account }}>
          <UI.InputLabel htmlFor="LedgerAccount">
            {t('ledger_account')}
          </UI.InputLabel>
          <UI.DebouncedInput {...form.text('ledger_account')} id="LedgerAccount" />
          <UI.ErrorMessages attribute={t('ledger_account')} errors={errors.ledger_account} />
        </UI.InputGroup>
      </UI.FormGrid>
    </>
  );
};

export default ProductInvoiceOptionsForm;
